import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../shared/api';

type Locale = 'hr' | 'en';

type DocDocRow = {
    company_id: number;
    company_code: string | null;
    company_name: string;
    base_url: string;
    site_id: string | null;
    username: string | null;
    has_password: boolean;
    has_api_key: boolean;
    is_enabled: boolean;
    last_tested_at: string | null;
    last_test_status: 'ok' | 'fail' | null;
    last_test_message: string | null;
};

type EditState = {
    base_url: string;
    site_id: string;
    username: string;
    password: string;
    api_key: string;
    is_enabled: boolean;
};

type KbSection = { title: string; body: string };

type ChatMessage =
    | { role: 'user'; content: string }
    | { role: 'assistant'; short: string; detail: string };

type AiChatResponse = {
    answer_short: string;
    answer_detail: string;
};

type Tab = 'overview' | 'config' | 'chat';

export function AdminDocDocSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [tab, setTab] = useState<Tab>('overview');

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📚</div>
                <div className="bi-module-hero-text">
                    <h1>140 · {t('DocDoc integracija', 'DocDoc integration')}</h1>
                    <p className="muted">{t('Vanjski document-management sustav za pohranu i workflow računa.', 'External document-management system for invoice storage and workflow.')}</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.25)', paddingBottom: 10 }}>
                {([
                    { id: 'overview' as Tab, icon: '📖', label: t('Što sustav može', 'What the system can do') },
                    { id: 'config' as Tab, icon: '🔌', label: t('Konfiguracija', 'Configuration') },
                    { id: 'chat' as Tab, icon: '💬', label: t('Pitaj AI', 'Ask AI') },
                ]).map((cfg) => {
                    const active = tab === cfg.id;
                    return (
                        <button
                            key={cfg.id}
                            type="button"
                            onClick={() => setTab(cfg.id)}
                            style={{
                                padding: '8px 14px',
                                borderRadius: 8,
                                border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                                color: active ? '#fffbeb' : 'var(--ad-ink)',
                                fontWeight: 600,
                                fontSize: '0.88rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <span>{cfg.icon}</span>
                            <span>{cfg.label}</span>
                        </button>
                    );
                })}
            </div>

            {tab === 'overview' ? <KnowledgeTab token={token} locale={locale} /> : null}
            {tab === 'config' ? <ConfigTab token={token} locale={locale} /> : null}
            {tab === 'chat' ? <ChatTab token={token} locale={locale} /> : null}
        </section>
    );
}

/* ── Tab 1: KB browser ─────────────────────────────────────────────── */

function KnowledgeTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [sections, setSections] = useState<KbSection[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const [search, setSearch] = useState('');

    useEffect(() => {
        api<{ sections: KbSection[]; error?: string }>('/admin/docdoc/knowledge', token)
            .then((r) => {
                setSections(r.sections);
                if (r.error) setError(r.error);
            })
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token]);

    const filtered = sections
        .map((s, i) => ({ ...s, idx: i }))
        .filter((s) => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q);
        });

    if (loading) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (sections.length === 0) return <p className="muted">{t('KB datoteka nije pronađena.', 'KB file not found.')}</p>;

    return (
        <div>
            <input
                type="search"
                placeholder={t('Pretraga…', 'Search…')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid rgba(var(--ad-soft-rgb),0.3)',
                    borderRadius: 8,
                    marginBottom: 14,
                    fontSize: '0.88rem',
                }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {filtered.map((s) => {
                    const isOpen = openIndex === s.idx;
                    return (
                        <div key={s.idx} style={{
                            border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                            borderRadius: 10,
                            background: isOpen ? 'rgba(255,251,235,0.6)' : '#fff',
                            overflow: 'hidden',
                        }}>
                            <button
                                type="button"
                                onClick={() => setOpenIndex(isOpen ? null : s.idx)}
                                style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 0,
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontWeight: 600,
                                    fontSize: '0.94rem',
                                    color: 'var(--ad-ink)',
                                }}
                            >
                                <span>{s.title}</span>
                                <span style={{ color: 'var(--ad-acc)' }}>{isOpen ? '▾' : '▸'}</span>
                            </button>
                            {isOpen ? (
                                <div style={{
                                    padding: '0 16px 14px',
                                    fontSize: '0.86rem',
                                    color: '#34383d',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.55,
                                }}>
                                    <MarkdownLite text={s.body} />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
                {filtered.length === 0 ? (
                    <p className="muted" style={{ textAlign: 'center', padding: '1.5rem' }}>
                        {t('Nema rezultata za pretraživanje.', 'No results.')}
                    </p>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Mini-markdown renderer — ne učitavamo cijeli markdown lib radi 5 elemenata.
 * Podržava: H3 (### naslov), bold (**txt**), inline code (`code`), liste (- item) i tablice.
 */
function MarkdownLite({ text }: { text: string }) {
    const lines = text.split('\n');
    const blocks: React.ReactNode[] = [];
    let buffer: string[] = [];
    let inTable = false;
    let tableRows: string[][] = [];

    const flushBuffer = () => {
        if (buffer.length === 0) return;
        const joined = buffer.join('\n');
        blocks.push(<p key={blocks.length}>{renderInline(joined)}</p>);
        buffer = [];
    };

    const flushTable = () => {
        if (tableRows.length === 0) return;
        const [header, ...rows] = tableRows;
        blocks.push(
            <div key={blocks.length} style={{ overflowX: 'auto', margin: '8px 0' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.82rem', width: '100%' }}>
                    <thead>
                        <tr>
                            {header.map((h, i) => (
                                <th key={i} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', padding: '6px 10px', textAlign: 'left', background: 'rgba(var(--ad-acc-rgb),0.1)' }}>
                                    {renderInline(h)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                {row.map((cell, ci) => (
                                    <td key={ci} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.25)', padding: '6px 10px', verticalAlign: 'top' }}>
                                        {renderInline(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>,
        );
        tableRows = [];
        inTable = false;
    };

    for (const line of lines) {
        if (line.startsWith('### ')) {
            flushBuffer();
            flushTable();
            blocks.push(<h3 key={blocks.length} style={{ fontSize: '0.95rem', margin: '10px 0 4px', color: 'var(--ad-acc-3)' }}>{line.slice(4)}</h3>);
            continue;
        }
        if (line.startsWith('- ')) {
            flushBuffer();
            flushTable();
            blocks.push(<li key={blocks.length} style={{ marginLeft: 18, fontSize: '0.86rem' }}>{renderInline(line.slice(2))}</li>);
            continue;
        }
        if (/^\|.+\|$/.test(line.trim())) {
            flushBuffer();
            const cells = line.trim().slice(1, -1).split('|').map((c) => c.trim());
            // Separator row: |---|---|
            if (cells.every((c) => /^:?-+:?$/.test(c))) continue;
            tableRows.push(cells);
            inTable = true;
            continue;
        }
        if (inTable) {
            flushTable();
        }
        if (line.trim() === '') {
            flushBuffer();
            continue;
        }
        if (line.startsWith('```')) {
            flushBuffer();
            continue; // ignoring fence delimiters; code block content goes through normal flow
        }
        buffer.push(line);
    }
    flushBuffer();
    flushTable();

    return <div>{blocks}</div>;
}

function renderInline(text: string): React.ReactNode {
    // Process **bold** and `code` together.
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;
    while (remaining.length > 0) {
        const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
        const codeMatch = remaining.match(/`([^`]+)`/);
        const boldIdx = boldMatch?.index ?? -1;
        const codeIdx = codeMatch?.index ?? -1;
        if (boldIdx === -1 && codeIdx === -1) {
            parts.push(remaining);
            break;
        }
        const useBold = codeIdx === -1 || (boldIdx !== -1 && boldIdx < codeIdx);
        if (useBold && boldMatch) {
            parts.push(remaining.slice(0, boldIdx));
            parts.push(<strong key={`b${key++}`}>{boldMatch[1]}</strong>);
            remaining = remaining.slice(boldIdx + boldMatch[0].length);
        } else if (codeMatch) {
            parts.push(remaining.slice(0, codeIdx));
            parts.push(
                <code key={`c${key++}`} style={{ background: 'rgba(var(--ad-acc-rgb),0.12)', padding: '1px 6px', borderRadius: 4, fontSize: '0.84em', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {codeMatch[1]}
                </code>,
            );
            remaining = remaining.slice(codeIdx + codeMatch[0].length);
        }
    }
    return <>{parts}</>;
}

/* ── Tab 2: Konfiguracija ──────────────────────────────────────────── */

function ConfigTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<DocDocRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [edit, setEdit] = useState<Record<number, EditState>>({});
    const [busy, setBusy] = useState<Record<number, 'saving' | 'testing' | undefined>>({});
    const [testResult, setTestResult] = useState<Record<number, { ok: boolean; message: string }>>({});

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const r = await api<{ rows: DocDocRow[] }>('/admin/docdoc/configs', token);
            setRows(r.rows);
            const next: Record<number, EditState> = {};
            r.rows.forEach((row) => {
                next[row.company_id] = {
                    base_url: row.base_url ?? 'https://demo.docdocc.hr',
                    site_id: row.site_id ?? '',
                    username: row.username ?? '',
                    password: '',
                    api_key: '',
                    is_enabled: row.is_enabled,
                };
            });
            setEdit(next);
        } catch (e) { setError(e instanceof Error ? e.message : ''); }
        finally { setLoading(false); }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    const update = (cid: number, patch: Partial<EditState>) => setEdit((m) => ({ ...m, [cid]: { ...m[cid], ...patch } }));

    const save = async (cid: number) => {
        const e = edit[cid];
        setBusy((b) => ({ ...b, [cid]: 'saving' }));
        try {
            await api(`/admin/docdoc/configs/${cid}`, token, {
                method: 'PATCH',
                body: JSON.stringify({
                    base_url: e.base_url || null,
                    site_id: e.site_id || null,
                    username: e.username || null,
                    password: e.password || null,
                    api_key: e.api_key || null,
                    is_enabled: e.is_enabled,
                }),
            });
            update(cid, { password: '', api_key: '' });
            await load();
        } catch (err) { setError(err instanceof Error ? err.message : ''); }
        finally { setBusy((b) => ({ ...b, [cid]: undefined })); }
    };

    const testConn = async (cid: number) => {
        setBusy((b) => ({ ...b, [cid]: 'testing' }));
        try {
            const r = await api<{ ok: boolean; message: string }>(`/admin/docdoc/configs/${cid}/test`, token, { method: 'POST' });
            setTestResult((m) => ({ ...m, [cid]: { ok: r.ok, message: r.message } }));
            await load();
        } catch (err) {
            setTestResult((m) => ({ ...m, [cid]: { ok: false, message: err instanceof Error ? err.message : '' } }));
        }
        finally { setBusy((b) => ({ ...b, [cid]: undefined })); }
    };

    return (
        <>
            <p className="muted" style={{ fontSize: '0.84rem', marginTop: 0 }}>
                {t('Per-company DocDoc kredencijali. Lozinka i API ključ se čuvaju enkriptirano.', 'Per-company DocDoc credentials. Password and API key stored encrypted.')}
            </p>
            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {rows.map((row) => {
                    const e = edit[row.company_id];
                    if (! e) return null;
                    const tr = testResult[row.company_id];
                    const isBusySave = busy[row.company_id] === 'saving';
                    const isBusyTest = busy[row.company_id] === 'testing';

                    return (
                        <div key={row.company_id} style={{
                            border: '1px solid rgba(var(--ad-soft-rgb),0.28)',
                            borderRadius: 14,
                            padding: '1rem 1.2rem',
                            background: row.is_enabled ? 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)' : '#fff',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.8rem' }}>
                                <div>
                                    <strong style={{ fontSize: '1.05rem', color: 'var(--ad-ink)' }}>{row.company_code ? `${row.company_code} · ` : ''}{row.company_name}</strong>
                                    {row.last_tested_at ? (
                                        <p style={{ margin: '2px 0 0', fontSize: '0.74rem', color: row.last_test_status === 'ok' ? '#1f7a36' : '#b14040' }}>
                                            {row.last_test_status === 'ok' ? '✓' : '✗'} {t('zadnji test', 'last test')}: {new Date(row.last_tested_at.replace(' ', 'T')).toLocaleString('hr-HR')} — {(row.last_test_message ?? '').slice(0, 80)}
                                        </p>
                                    ) : null}
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={e.is_enabled} onChange={(ev) => update(row.company_id, { is_enabled: ev.target.checked })} />
                                    <strong style={{ color: e.is_enabled ? '#1f7a36' : '#94a3b8' }}>{e.is_enabled ? t('Aktivna', 'Enabled') : t('Neaktivna', 'Disabled')}</strong>
                                </label>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Base URL', 'Base URL')}
                                    <input value={e.base_url} onChange={(ev) => update(row.company_id, { base_url: ev.target.value })} placeholder="https://demo.docdocc.hr" />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    Site ID
                                    <input value={e.site_id} onChange={(ev) => update(row.company_id, { site_id: ev.target.value })} placeholder="auro-domus-money-shop-doo" />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Korisničko ime', 'Username')}
                                    <input value={e.username} onChange={(ev) => update(row.company_id, { username: ev.target.value })} placeholder="ime.prezime" />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Lozinka', 'Password')} {row.has_password ? <span style={{ fontSize: '0.7rem', color: '#1f7a36' }}>● {t('postavljeno', 'set')}</span> : null}
                                    <input type="password" autoComplete="new-password" value={e.password} onChange={(ev) => update(row.company_id, { password: ev.target.value })} placeholder={row.has_password ? '••••••••' : ''} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    API key {row.has_api_key ? <span style={{ fontSize: '0.7rem', color: '#1f7a36' }}>● {t('postavljeno', 'set')}</span> : null}
                                    <input type="password" autoComplete="new-password" value={e.api_key} onChange={(ev) => update(row.company_id, { api_key: ev.target.value })} placeholder={row.has_api_key ? '••••••••' : ''} />
                                </label>
                            </div>
                            <p className="muted" style={{ fontSize: '0.74rem', marginTop: 8 }}>
                                {t('Ostavi prazno za zadržavanje postojeće vrijednosti. Lozinka i API ključ se nikad ne prikazuju natrag.', 'Leave blank to keep the existing value. Password and API key are never shown back.')}
                            </p>

                            <div style={{ display: 'flex', gap: 6, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                <button type="button" className="compact" disabled={isBusySave} onClick={() => save(row.company_id)}>
                                    {isBusySave ? '⏳ ' + t('Spremam…', 'Saving…') : '💾 ' + t('Spremi', 'Save')}
                                </button>
                                <button type="button" className="secondary compact" disabled={isBusyTest || ! row.has_password || ! row.has_api_key || ! row.site_id || ! row.username} onClick={() => testConn(row.company_id)}>
                                    {isBusyTest ? '⏳ ' + t('Testiram…', 'Testing…') : '🔌 ' + t('Test konekcije', 'Test connection')}
                                </button>
                                {tr ? (
                                    <span style={{ fontSize: '0.82rem', color: tr.ok ? '#1f7a36' : '#b14040' }}>{tr.ok ? '✓' : '✗'} {tr.message.slice(0, 100)}</span>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

/* ── Tab 3: AI chat ──────────────────────────────────────────────── */

function ChatTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            role: 'assistant',
            short: t('Pitaj me što god te zanima o DocDoc-u.', 'Ask me anything about DocDoc.'),
            detail: t('Mogu odgovoriti na pitanja o tome što sustav može, kako se koriste pojedini endpointi, što se nalazi u našim šifrarnicima i kakvi su tipični tokovi rada.', 'I can explain what the system does, how endpoints work, what is stored in our master data, and what typical workflows look like.'),
        },
    ]);
    const [question, setQuestion] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    async function submit() {
        const q = question.trim();
        if (!q || busy) return;
        setMessages((m) => [...m, { role: 'user', content: q }]);
        setQuestion('');
        setBusy(true);
        setError('');
        try {
            const r = await api<AiChatResponse>('/ai/chat', token, {
                method: 'POST',
                body: JSON.stringify({
                    question: q,
                    module: 'docdoc',
                    filters: {},
                }),
            });
            setMessages((m) => [...m, { role: 'assistant', short: r.answer_short, detail: r.answer_detail }]);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div>
            <div style={{
                maxHeight: 460,
                minHeight: 280,
                overflowY: 'auto',
                background: '#fff',
                border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                borderRadius: 10,
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
            }}>
                {messages.map((m, i) => (
                    m.role === 'user' ? (
                        <div key={i} style={{ alignSelf: 'flex-end', maxWidth: '85%', background: 'rgba(var(--ad-acc-rgb),0.18)', color: '#34383d', padding: '8px 12px', borderRadius: 12, fontSize: '0.88rem' }}>
                            {m.content}
                        </div>
                    ) : (
                        <div key={i} style={{ alignSelf: 'flex-start', maxWidth: '92%', background: 'rgba(255,251,235,0.6)', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', padding: '10px 14px', borderRadius: 12 }}>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: '#34383d' }}>{m.short}</p>
                            {m.detail ? <p style={{ margin: '4px 0 0', fontSize: '0.84rem', color: '#6b6e74', lineHeight: 1.5 }}>{m.detail}</p> : null}
                        </div>
                    )
                ))}
                {busy ? <p className="muted" style={{ fontStyle: 'italic', fontSize: '0.82rem' }}>{t('Razmišljam…', 'Thinking…')}</p> : null}
                {error ? <p className="error">{error}</p> : null}
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
                    placeholder={t('Npr. „Može li DocDoc raditi OCR?"', 'e.g. "Can DocDoc do OCR?"')}
                    disabled={busy}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, fontSize: '0.88rem' }}
                />
                <button type="button" onClick={() => void submit()} disabled={busy || !question.trim()} className="compact">
                    {busy ? '⏳' : t('Pošalji', 'Send')}
                </button>
            </div>
        </div>
    );
}
